import cloneDeep from 'lodash.clonedeep';
import { v4 as uuidv4 } from 'uuid';
import { EditorDocument, PageNumbering } from 'entities/document';

interface MutableNode {
  type?: string;
  id?: string;
  uuid?: string;
  bookmarkId?: string;
  text?: string;
  children?: MutableNode[];
  [key: string]: unknown;
}

type IdMapping = Record<string, unknown>;

export const calulcateCurrentPage = (
  pDocument: EditorDocument,
  section: { content?: { uuid?: string }[] },
  pageId: string,
  tableOfContentSection: { range?: { start?: number } } | null | undefined
) => {
  const pageIndex = section.content?.findIndex((page) => page.uuid === pageId) ?? -1;
  const physicalPage = (tableOfContentSection?.range?.start ?? 0) + pageIndex;

  const offset = calculatePageOffset(pDocument.content?.pageNumbering);

  return { physicalPage, documentPage: physicalPage + offset };
};

export const calculatePageOffset = (pageNumberSettings?: PageNumbering) => {
  if (!pageNumberSettings) {
    return 0;
  }
  return pageNumberSettings.countFrom - pageNumberSettings.showFrom;
};

export const replaceTypeId = (page: MutableNode, replaceFn: (node: MutableNode, idMappings: IdMapping[]) => MutableNode) => {
  if (typeof replaceFn !== 'function') {
    console.error('replaceFn must be a function');
    return { clonedPage: page, idMappings: [] };
  }

  const deepclone: MutableNode = cloneDeep(page);
  const idMappings: IdMapping[] = [];
  deepclone.children = deepclone.children?.map((node) => replaceFn(node, idMappings));
  return { clonedPage: deepclone, idMappings };
};

const replaceLinksAndBookmarks = (node: MutableNode, idMappings: IdMapping[]) => {
  if (node.type === 'link') {
    const oldId = node.id;
    const newId = uuidv4();
    const bookmarkId = node.bookmarkId;

    node.id = `lk-${newId}`;
    idMappings.push({ oldId, newId: `lk-${newId}`, bookmarkId });
  }
  if (node.type === 'bookmark') {
    const oldId = node.id;
    const newId = uuidv4();

    node.id = `bm-${newId}`;
    idMappings.push({ oldId, newId });
  }

  if (node.children) {
    node.children = node.children.map((child) => {
      if (typeof child.text !== 'undefined') {
        return child;
      }
      return replaceLinksAndBookmarks(child, idMappings);
    });
  }

  return node;
};

// Main function to clone and return the updated page with the content replaced
export const cloneDeepPage = (pageContent: MutableNode) => {
  const { clonedPage, idMappings } = replaceTypeId(pageContent, replaceLinksAndBookmarks);
  const clonedPagedWithNewUUID = generateNewUUID(clonedPage);
  const newPageUUID = uuidv4();
  clonedPagedWithNewUUID.uuid = newPageUUID;
  return { clonedPage: clonedPagedWithNewUUID, pageId: newPageUUID, idMappings };
};

const bookmarksAndLinks = (node: MutableNode, idMappings: IdMapping[]) => {
  if (node.type === 'link' || node.type === 'bookmark') {
    idMappings.push({ id: node.id, type: node.type });
  }
  if (node.children) {
    node.children = node.children.map((child) => bookmarksAndLinks(child, idMappings));
  }

  return node;
};

export const getBookmarkAndLinks = (pageContent: MutableNode) => {
  const { idMappings } = replaceTypeId(pageContent, bookmarksAndLinks);

  return idMappings;
};

export const generateNewUUID = (content: MutableNode): MutableNode => {
  if (content?.uuid) {
    content.uuid = uuidv4();
  }
  if (content?.children) {
    content.children = content.children.map(generateNewUUID);
  }
  return content;
};
